import * as React from 'react';
import { MarketingInput } from '@wix/answers-common-components';
import { RenderHtml } from '../../../helpers/render-html';
import { NewTenantParams } from '../../../helpers/api.client';
import { Logger, withLogger } from '../../../helpers/bi';
import { BIEvent } from '../../../helpers/bi/event-type';
import { loginUrl, isEmailValid, checkIfMobile, isPassValid } from '../../../helpers/utils';
import * as classNames from 'classnames';
import { BUSINESS_EMAIL_LABEL, withTranslator } from '../../../helpers/shared';
import { TranslateFn } from '@wix/answers-lib';

export type StartSignupProps = {
	onSubmit: (params: Partial<NewTenantParams>) => void;
} & withLogger & withTranslator;

export type StartSignupState = {
	recaptchaValue: string;
	fullName: string;
	fullNameError: string;
	email: string;
	emailError: string;
	password: string;
	passwordError: string;
	isMobile: boolean;
};

export class StartSignup extends React.PureComponent<StartSignupProps, StartSignupState> {
	logger: Logger = this.props.logger;
	compTranslate: TranslateFn = (key, params) => this.props.t(`marketing-site.signup-form.signup.${key}`, params);
	requiredError =  this.compTranslate('errors.required-field');

	state: StartSignupState = {
		recaptchaValue: '',
		fullName: '',
		fullNameError: '',
		email: '',
		emailError: '',
		password: '',
		passwordError: '',
		isMobile: checkIfMobile()
	};

	maxPasswordLength = 6;

	validateName = () => {
		const {compTranslate} = this;
		const fullName = this.state.fullName.trim();
		let result = '';

		if (!fullName) {
			result = compTranslate('errors.name-required');
		} else if (fullName.length < 3) {
			result = compTranslate('errors.name-min-length');
		}

		this.setState({fullNameError: result});
		return result;
	}

	validateEmail = () => {
		const {compTranslate} = this;
		const email = this.state.email;
		let result = '';

		if (!email) {
			result = compTranslate('errors.email-required');
		} else if (!isEmailValid(email)) {
			result = compTranslate('errors.email-invalid');
		}

		this.setState({emailError: result});
		return result;
	}

	validatePassword = () => {
		const {compTranslate} = this;
		const pw = this.state.password;
		let result = '';

		if (!pw) {
			result = compTranslate('errors.password-required');
		} else if (pw.length < this.maxPasswordLength) {
			result = compTranslate('errors.password-min-length');
		} else if (!isPassValid(pw)) {
			result = compTranslate('errors.password-rules-unmet');
		}

		this.setState({passwordError: result});
		return result;
	}

	isValid = () => {
		const nameError = this.validateName();
		const emailError = this.validateEmail();
		const pwdError = this.validatePassword();

		return !nameError && !emailError && !pwdError;
	}

	getInvalidFields = () => {
		return [
			this.validateName() ? 'fullname' : '',
			this.validateEmail() ? 'email' : '',
			this.validatePassword() ? 'password' : ''
		]
		.filter((i) => !!i)
		.join(', ');
	}

	isNotEmpty = () => {
		const {state} = this;
		return !!state.fullName && !!state.email && !!state.password;
	}

	onSubmit = (e: any) => {
		const {state, props, logger} = this;

		e.stopPropagation();
		e.preventDefault();

		if (state.isMobile) {
			this.setState({isMobile: false});
			return;
		}

		const params: Partial<NewTenantParams> = {
			userEmail: state.email,
			userPassword: state.password,
			userName: state.fullName,
		};

		logger.logEvent(BIEvent.SIGNUP_CLICK_STEP1);

		if (this.isValid()) {
			logger.logEvent(BIEvent.SIGNUP_SUCCESS_STEP1);
			props.onSubmit(params);
		} else {
			const invalidFields = this.getInvalidFields();
			logger.logEvent(BIEvent.SIGNUP_FAILED_STEP1, {error: `Form validation failed in fields - [${invalidFields}]`});
		}
	}

	fullNameOnChange = (val: string) => this.setState({fullName: val, fullNameError: ''});
	emailOnChange = (val: string) => this.setState({email: val, emailError: ''});
	passwordOnChange = (val: string) => this.setState({password: val.trim(), passwordError: ''});

	render () {
		const {state, compTranslate} = this;

		const formComp = (
			<div className='start-signup-form'>
				<form className='form-container' noValidate={true} onSubmit={this.onSubmit}>
					<MarketingInput className='fullname' error={state.fullNameError} value={state.fullName} type='text' label={compTranslate('fields.name')} onChange={this.fullNameOnChange} onBlur={this.validateName} startFocused={true}/>
					<MarketingInput className='email' error={state.emailError} value={state.email} type='email' label={BUSINESS_EMAIL_LABEL} onChange={this.emailOnChange} onBlur={this.validateEmail} />
					<MarketingInput className='pass' error={state.passwordError} value={state.password} type='password' label={compTranslate('fields.password')} onChange={this.passwordOnChange} onBlur={this.validatePassword} />
					<div className='signup-button-container'>
						<button className='signup primary-btn' onClick={this.onSubmit}>{compTranslate('signup-btn')}</button>
						<button className='hollow-btn signup-mobile-only' onClick={this.onSubmit}>{compTranslate('mobile-signup-btn')}</button>
					</div>
				</form>
			</div>
		);

		return (
			<div className={classNames('start-signup-container')}>
				<header>
					<h1>{compTranslate('title-step1')}</h1>
					<h2 className='helper'>
						{compTranslate('have-account')}
						<a className='login-cta' href={loginUrl}>{compTranslate('login')}</a>
					</h2>
				</header>
				<main className='start-signup-form-container'>{formComp}</main>
				<div id='signup-terms'><RenderHtml html={compTranslate('new-footer')}/></div>
			</div>
		);
	}
}
